/**
 * The trader.
 *
 * Every station has one, and every trader carries the same eight essentials
 * whatever else it has. That floor is deliberate and it is a safety net rather
 * than a convenience: between them those parts build a hull that flies, so a
 * player who has lost everything is never stranded at a station looking at a
 * shop that cannot sell them a way out.
 *
 * On top of the floor, stock is seeded off the station — the same trader has
 * the same shelves every time you visit, and different traders are worth
 * visiting. Nothing here is consumed yet; depletion belongs with the
 * twenty-four-hour station deltas in the technical plan, and this is the
 * catalogue that will be depleted.
 */
import { COMPONENT_TYPES } from '../components.js';
import { trinketStock } from './trinkets.js';
import { DEFAULT_LINE_COLOR } from '../ship/upgrades.js';
import { Rng } from '../world/rng.js';
import { getClusterSummaries } from '../world/generate.js';
import { LINE_HEALTH } from '../ship/upgrades.js';
import { LINELITS_PER_UNIT } from './fabricator.js';
/**
 * What a trader adds over what line is actually worth.
 *
 * Twenty per cent, and it is load-bearing rather than flavour. Line melts back
 * down at exactly its length — see `LINELITS_PER_UNIT` — so a trader selling at
 * cost would be an infinite money press: buy, shred, repeat. A markup makes the
 * round trip lose money, which is the only thing that stops it.
 *
 * It also settles what the fabricator is *for*. Buying is convenient and
 * slightly dear; fabricating is exactly break-even and never runs out. So a
 * trader is where you go for what you need now, and a fabricator is where you
 * go when you have linelits and time.
 */
export const TRADER_MARKUP = 1.2;
/** Linelits per unit of line, at a trader. */
export const TRADER_LINE_RATE = LINELITS_PER_UNIT * TRADER_MARKUP;
/**
 * What a component costs.
 *
 * On the same scale as line now: a twenty-unit line is twenty-four linelits and
 * an engine is twenty, so a hull comes out roughly half structure and half
 * fittings — which is the balance the editor is built around. Shields and
 * scanners cost several times a drive because they are what you fit once you
 * have a ship, rather than what makes one.
 */
export const COMPONENT_PRICES = {
    vault: 10,
    turretSmall: 10,
    thrusterSmall: 12,
    landingGear: 15,
    engine: 20,
    thruster: 25,
    pilotSeat: 25,
    reactor: 30,
    collector: 40,
    thrusterLarge: 50,
    scanner: 70,
    shield: 100,
};
/**
 * What a joint costs.
 *
 * Flat, because a joint is a joint — there is no size to it. Priced against
 * line rather than against parts: about four units' worth, so growing a hull
 * outward costs real money without being the thing that stops you.
 *
 * There is no fabricator route to these. Line can be melted and re-laid; a
 * joint is the one structural piece you have to buy.
 */
export const JOINT_PRICE = 5;
export function linePrice(length) {
    return length * TRADER_LINE_RATE;
}
/**
 * The one consumable that matters so far.
 *
 * A cluster jump burns one of these. It is the only thing standing between a
 * player and the rest of the galaxy, so it is deliberately not something you
 * keep a drawer of: dear enough to be a decision, heavy enough to notice in a
 * small hold, and not on every shelf.
 *
 * Priced above every component the trader sells. A shield is 100 and a scanner
 * 70; a core is the difference between one cluster and all of them, and pricing
 * it alongside a part would say it was one.
 */
export const WARP_OVERDRIVE_CORE = {
    id: 'warp-overdrive-core',
    label: 'Warp Overdrive Core',
    weight: 4,
    price: 150,
};
/**
 * A single shout, heard by everyone in the system.
 *
 * Spent to send one `/alert`, which reaches every pilot in the system at once
 * rather than only whoever happens to be reading the local channel. The point is
 * that it is *rare and gone*: something worth interrupting strangers for is
 * worth having paid three hundred linelits and a hold slot to say.
 *
 * Light enough that carrying one against the day you need it is a real option --
 * which is what makes it interesting to carry at all.
 */
export const RADIO_PULSE_BEACON = {
    id: 'radio-pulse-beacon',
    label: 'Radio Pulse Beacon',
    weight: 0.4,
    price: 300,
};
/**
 * How often a station has a beacon in.
 *
 * Much rarer than a core, and deliberately not underwritten by a depot the way
 * cores are: nothing is soft-locked by never finding one. A player who cannot
 * buy a beacon is a player who cannot shout, which is an inconvenience rather
 * than a dead end.
 */
const BEACON_STOCK_CHANCE = 0.07;
/**
 * How often a station has one in.
 *
 * Semi-rare: common enough that hunting for one is a short errand rather than a
 * campaign, rare enough that finding a station with two is worth remembering.
 */
const CORE_STOCK_CHANCE = 0.28;
/**
 * The one station in each cluster that always has cores in.
 *
 * ── This is a soft-lock fix, not a nicety ───────────────────────────────────
 *
 * Leaving a cluster costs an overdrive core, and a core can only be bought. At
 * 28% per station, a cluster with few stations can roll none at all — and a
 * player who flies into one is trapped there permanently, with no way to tell
 * from inside the game that it has happened.
 *
 * Measured on the dev seed rather than assumed: clusters carry between 5 and 53
 * stations, and of the sixty thinnest, **three had no core seller anywhere** —
 * Doraeus with 7 stations, Lyriair with 9, Umbukios with 11. Three dead ends in
 * one galaxy, and the same arithmetic makes them likely in any seed.
 *
 * So one station per cluster stocks them by rule. Picked from the cluster's
 * *summaries*, which carry a station count without generating a system, so this
 * costs a walk over one cluster's index and nothing more.
 *
 * Deliberately not announced anywhere. A depot looks like any other trader that
 * happens to have cores in; this is a floor under the generator, not a feature.
 */
export function coreDepotOf(seed, clusterId) {
    // Shape-checked rather than trusted. A station id is the only thing callers
    // have, and one that is not from this galaxy at all — a fixture, a stale save
    // — must come back "no depot" rather than throwing out of a shop.
    if (!/^c\d+$/.test(clusterId))
        return null;
    const hosts = getClusterSummaries(seed, clusterId).filter((system) => system.stationCount > 0);
    if (hosts.length === 0)
        return null;
    const rng = Rng.forEntity(seed, clusterId, 'core-depot');
    const system = hosts[Math.floor(rng.float() * hosts.length)];
    const berth = Math.floor(rng.float() * system.stationCount);
    // Station ids are `<system>-t<berth>`, so the id can be built rather than
    // looked up — which is what keeps this off the generation path.
    return `${system.id}-t${berth}`;
}
/** Whether this trader is their cluster's depot. Derived from the id alone. */
export function isCoreDepot(seed, stationId) {
    const clusterId = stationId.split('-')[0];
    return clusterId ? coreDepotOf(seed, clusterId) === stationId : false;
}
/**
 * The floor every trader carries.
 *
 * Line, and only line. It used to guarantee a whole ship's worth of parts so a
 * ruined player could rebuild — but losing a hull now hands you a working
 * starter, so the fittings are already taken care of and a shop stocking them
 * regardless was solving a problem that no longer exists.
 *
 * Short line stays guaranteed because it is the one thing you cannot improvise:
 * a fabricator will make any length you can pay for, but not every station has
 * one.
 */
const ESSENTIALS = [
    { length: 10, quantity: 2 },
    { length: 15, quantity: 1 },
];
/** Joints every trader keeps. Line is no use without somewhere to end it. */
const ESSENTIAL_JOINTS = 1;
/** How many extra kinds of line and part a trader carries beyond the floor. */
const EXTRA_LINES = { min: 2, max: 6 };
const EXTRA_COMPONENTS = { min: 2, max: 5 };
/** The longest line a trader stocks. Past this, see the fabricator. */
const MAX_TRADED_LENGTH = 50;
/**
 * What this station's trader has on the shelves.
 *
 * Seeded off the station rather than rolled per visit: a trader you found a
 * long line at should still have it tomorrow, or there is no reason to remember
 * where anything is.
 */
export function traderStock(seed, stationId) {
    const random = Rng.forEntity(seed, stationId, 'trader');
    const items = [];
    for (const essential of ESSENTIALS) {
        items.push({
            kind: 'line',
            id: `line-${essential.length}`,
            length: essential.length,
            strength: 1,
            quantity: essential.quantity,
            price: linePrice(essential.length),
        });
    }
    items.push({
        kind: 'joint',
        id: 'joint',
        quantity: ESSENTIAL_JOINTS,
        price: JOINT_PRICE,
    });
    /** Folded into whatever is already stocked, so the floor is a floor. */
    const stockLine = (length, quantity) => {
        const existing = items.find((item) => item.kind === 'line' && item.length === length);
        if (existing)
            existing.quantity += quantity;
        else {
            items.push({
                kind: 'line',
                id: `line-${length}`,
                length,
                strength: 1,
                quantity,
                price: linePrice(length),
            });
        }
    };
    const stockComponent = (type, quantity) => {
        const existing = items.find((item) => item.kind === 'component' && item.type === type);
        if (existing)
            existing.quantity += quantity;
        else {
            items.push({
                kind: 'component',
                id: `part-${type}`,
                type,
                quantity,
                price: COMPONENT_PRICES[type],
            });
        }
    };
    const count = ({ min, max }) => min + Math.floor(random.float() * (max - min + 1));
    for (let i = 0; i < count(EXTRA_LINES); i++) {
        // Whole units only: a length you cannot place is not stock, it is a puzzle.
        stockLine(1 + Math.floor(random.float() * MAX_TRADED_LENGTH), 1 + Math.floor(random.float() * 3));
    }
    // Overdrive cores, when this station happens to have them. Never more than
    // two: a shelf with a stack of them would make crossing clusters an errand
    // you do once and forget, which is the opposite of the point.
    // Either the roll came up, or this is the cluster's guaranteed depot.
    if (random.float() < CORE_STOCK_CHANCE || isCoreDepot(seed, stationId)) {
        items.push({
            kind: 'resource',
            id: WARP_OVERDRIVE_CORE.id,
            label: WARP_OVERDRIVE_CORE.label,
            weight: WARP_OVERDRIVE_CORE.weight,
            quantity: random.float() < 0.2 ? 2 : 1,
            price: WARP_OVERDRIVE_CORE.price,
        });
    }
    // A beacon, rarely, and never more than one. Two on a shelf would make the
    // thing that is supposed to be spent carefully into something you stock up on.
    if (random.float() < BEACON_STOCK_CHANCE) {
        items.push({
            kind: 'resource',
            id: RADIO_PULSE_BEACON.id,
            label: RADIO_PULSE_BEACON.label,
            weight: RADIO_PULSE_BEACON.weight,
            quantity: 1,
            price: RADIO_PULSE_BEACON.price,
        });
    }
    // A few more joints, sometimes. Never fewer than the floor.
    const spare = items.find((item) => item.kind === 'joint');
    if (spare)
        spare.quantity += Math.floor(random.float() * 4);
    for (let i = 0; i < count(EXTRA_COMPONENTS); i++) {
        const type = COMPONENT_TYPES[Math.floor(random.float() * COMPONENT_TYPES.length)];
        stockComponent(type, 1 + Math.floor(random.float() * 2));
    }
    /*
     * And whatever curios this station deals in.
     *
     * On the same shelf as everything else so the existing buy path, the shelf's
     * depletion tracking and the restock window all apply without knowing there is
     * anything special about them — a trinket is bought exactly the way a reactor
     * is. What it costs is the station's own opinion; see `trinkets.ts`.
     */
    for (const offer of trinketStock(seed, stationId)) {
        items.push({
            kind: 'trinket',
            id: offer.trinket.id,
            label: offer.trinket.name,
            weight: offer.trinket.weight,
            quantity: offer.quantity,
            price: offer.price,
            base: offer.trinket.base,
        });
    }
    // Lines by length, then parts by price, the one consumable next, and the
    // curios last — they are the thing you browse rather than the thing you came
    // for, and putting them above the fittings would bury what a trader is *for*.
    const order = { line: 0, joint: 1, component: 2, resource: 3, trinket: 4 };
    return items.sort((a, b) => {
        if (a.kind !== b.kind)
            return order[a.kind] - order[b.kind];
        if (a.kind === 'line' && b.kind === 'line')
            return a.length - b.length;
        return a.price - b.price;
    });
}
/** A traded line, as it lands in cargo. */
export function tradedLine(item) {
    return {
        id: `${item.id}-${Math.random().toString(36).slice(2, 8)}`,
        length: item.length,
        health: LINE_HEALTH,
        strength: item.strength,
        color: DEFAULT_LINE_COLOR,
    };
}
//# sourceMappingURL=trader.js.map